import PickButton from './PickButton'
import { formatOdds, calculateRiskPoints, calculateRewardPoints } from '../../lib/scoring'
import PickReactions from '../social/PickReactions'

function formatGameTime(dateStr) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) +
    ' · ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function formatLiveStatus(game) {
  const parts = ['LIVE']
  if (game.period || game.clock) {
    const sportKey = game.sports?.key || ''
    let periodLabel = ''
    if (game.period) {
      if (sportKey === 'basketball_ncaab') {
        periodLabel = `H${game.period}`
      } else if (sportKey.includes('basketball') || sportKey.includes('football')) {
        periodLabel = `Q${game.period}`
      } else if (sportKey.includes('baseball')) {
        periodLabel = game.period
      } else if (sportKey.includes('hockey')) {
        periodLabel = `P${game.period}`
      } else if (sportKey.includes('soccer')) {
        periodLabel = game.period === '1' ? '1H' : '2H'
      } else {
        periodLabel = `P${game.period}`
      }
    }
    const showClock = game.clock && !sportKey.includes('baseball')
    const detail = [periodLabel, showClock ? game.clock : null].filter(Boolean).join(' ')
    if (detail) parts.push(detail)
  }
  return parts.join(' · ')
}

export default function GameCard({ game, userPick, onPick, onUndoPick, isSubmitting, reactions, parlayMode, parlayPickedTeam, onParlayToggle, onCardClick, isFirstCard, hasInjuryData, onInjuryClick, transparent, sportKey }) {
  const isLocked = game.status !== 'upcoming'
  const isFinal = game.status === 'final'
  const isLive = game.status === 'live'
  const isPostponed = game.status === 'postponed'
  const hasLiveScores = isLive && game.live_home_score != null

  function getButtonState(side) {
    if (parlayMode) {
      if (parlayPickedTeam === side) return 'selected'
      return isLocked ? 'locked' : 'default'
    }
    if (!userPick) return isLocked ? 'locked' : 'default'
    if (userPick.picked_team !== side) {
      if (isFinal) return 'default'
      if (userPick.status === 'locked') return 'locked'
      return 'default'
    }
    // This side is picked
    if (isPostponed) return 'postponed'
    if (isFinal) {
      if (userPick.is_correct === null) return 'push'
      return userPick.is_correct ? 'correct' : 'incorrect'
    }
    if (userPick.status === 'locked') return 'locked-picked'
    return 'selected'
  }

  function handleClick(side, e) {
    e.stopPropagation()
    if (parlayMode) {
      onParlayToggle?.(game.id, side, game)
      return
    }
    if (!onPick) return
    // If clicking the same team that's already picked (and still pending), undo it
    if (userPick?.picked_team === side && userPick?.status === 'pending') {
      onUndoPick?.(game.id)
    } else {
      onPick(game.id, side)
    }
  }

  return (
    <div
      id={`game-${game.id}`}
      {...(isFirstCard ? { 'data-onboarding': 'game-card' } : {})}
      onClick={hasInjuryData ? () => onInjuryClick?.() : onCardClick ? () => onCardClick() : undefined}
      className={`bg-bg-primary/15 backdrop-blur-sm rounded-2xl border ${userPick?.status === 'locked' ? 'border-accent' : 'border-text-primary/20'} p-4 overflow-hidden${hasInjuryData || onCardClick ? ' cursor-pointer' : ''}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-text-muted uppercase tracking-wider">
          {game.sports?.name || 'NFL'}
        </span>
        <span className={`text-xs ${isPostponed ? 'text-yellow-500 font-semibold' : 'text-text-muted'}`}>
          {isPostponed
            ? 'Postponed'
            : isFinal
              ? 'Final'
              : isLive
                ? formatLiveStatus(game)
                : formatGameTime(game.starts_at)
          }
        </span>
      </div>

      <div className="flex gap-3">
        <div className="flex-1 min-w-0">
          <PickButton
            team={game.away_team}
            rank={game.away_rank}
            odds={game.away_odds}
            score={isFinal ? game.away_score : hasLiveScores ? game.live_away_score : null}
            isLive={hasLiveScores && !isFinal}
            state={getButtonState('away')}
            disabled={isLocked || isSubmitting}
            onClick={(e) => handleClick('away', e)}
            sportKey={sportKey}
            multiplier={userPick?.picked_team === 'away' ? (userPick.multiplier || 1) : 1}
          />
          {isFinal && (
            <div className="text-center text-xs text-text-muted mt-1">
              {game.away_odds != null
                ? formatOdds(game.away_odds)
                : userPick?.picked_team === 'away' && userPick?.odds_at_pick != null
                  ? formatOdds(userPick.odds_at_pick)
                  : ''}
            </div>
          )}
        </div>
        <div className="flex items-center text-text-muted text-xs font-semibold">@</div>
        <div className="flex-1 min-w-0">
          <PickButton
            team={game.home_team}
            rank={game.home_rank}
            odds={game.home_odds}
            score={isFinal ? game.home_score : hasLiveScores ? game.live_home_score : null}
            isLive={hasLiveScores && !isFinal}
            state={getButtonState('home')}
            disabled={isLocked || isSubmitting}
            onClick={(e) => handleClick('home', e)}
            sportKey={sportKey}
            multiplier={userPick?.picked_team === 'home' ? (userPick.multiplier || 1) : 1}
          />
          {isFinal && (
            <div className="text-center text-xs text-text-muted mt-1">
              {game.home_odds != null
                ? formatOdds(game.home_odds)
                : userPick?.picked_team === 'home' && userPick?.odds_at_pick != null
                  ? formatOdds(userPick.odds_at_pick)
                  : ''}
            </div>
          )}
        </div>
      </div>

      {/* A SETTLED pick reports its own result, even when the game row is
          still 'postponed'. Pick status is the more specific fact, and the
          two can legitimately disagree: a postponed game that is never made
          up inside its window settles its picks as pushes, and the game row
          stays postponed forever because it is true.

          This used to be the other way round — isPostponed won outright and
          the settled branch excluded postponed games — so a pushed pick sat
          on "pick will settle when rescheduled" permanently, a sentence that
          was false the moment it settled. */}
      {userPick?.status === 'settled' && userPick.points_earned !== null ? (
        <div className={`mt-3 text-center text-sm font-semibold ${userPick.points_earned > 0 ? 'text-correct' : userPick.points_earned < 0 ? 'text-incorrect' : 'text-text-muted'}`}>
          {userPick.is_correct === null
            // is_correct null on a settled pick IS the push marker — a tie,
            // or a game that never happened. "0 pts" reads as a loss that
            // scored nothing rather than a bet that was returned.
            ? (isPostponed ? 'Push — game postponed' : 'Push')
            : `${userPick.points_earned > 0 ? '+' : ''}${userPick.points_earned} pts`}
          {/* A multiplied pick had no marker anywhere once it settled. The
              buttons swap odds for the final score, which takes OddsDisplay
              and its badge off screen, and this line only ever printed the
              points — so the one moment worth celebrating, landing a 4x, read
              exactly like any other win.

              Shown on losses too: a 4x loss costs four times as much, and the
              number makes more sense with the reason attached. Not shown on a
              push, where the stake came back and the multiplier is moot. */}
          {userPick.is_correct !== null && userPick.multiplier > 1 && (
            <span className="ml-2 text-base font-bold text-accent align-middle">
              {userPick.multiplier}x
            </span>
          )}
        </div>
      ) : isPostponed && userPick ? (
        <div className="mt-3 text-center text-sm font-semibold text-yellow-500">
          Game postponed — pick will settle when rescheduled
        </div>
      ) : null}

      {userPick?.status === 'locked' && userPick.odds_at_pick != null && (
        <div className="mt-3 text-center text-sm text-text-muted">
          <span className="text-incorrect">-{userPick.risk_at_submission || calculateRiskPoints(userPick.odds_at_pick)}</span>
          {' / '}
          <span className="text-correct">+{userPick.reward_at_submission || calculateRewardPoints(userPick.odds_at_pick)}</span>
          {userPick.multiplier > 1 && (
            <span className="ml-1.5 text-[10px] font-bold text-accent px-1.5 py-0.5 rounded">
              {userPick.multiplier}x
            </span>
          )}
        </div>
      )}


      {reactions?.length > 0 && (
        <div className="mt-2">
          <PickReactions pickId={userPick?.id} compact reactions={reactions} />
        </div>
      )}
    </div>
  )
}
